import {
  DuviSharedAdminCard as Card,
  DuviSharedAdminCardHeader as CardHeader,
  DuviSharedAdminCardBody as CardBody,
  DuviSharedAdminBadge as Badge,
  DuviSharedAdminStatusPill as StatusPill,
  DuviSharedAdminAvatar as Avatar,
  DuviSharedAdminTextarea as Textarea,
  DuviSharedAdminSwitch as Switch,
  DuviSharedAdminButton as Button,
  DuviSharedAdminSelect as Select,
  DuviSharedAdminFormField as FormField,
  DuviSharedAdminIcon as Icon,
  DuviSharedAdminTestIds,
} from "@duvi-agency/ui";
import { DuviCoreAdminMonoChip as MonoChip } from "../../components/duvi-core-admin-kit";
import type { TicketDetail } from "../../api/client";
import {
  DUVI_CORE_ADMIN_TICKET_STATUS_SELECT,
  DUVI_CORE_ADMIN_TICKET_PRIORITY_SELECT,
  duviCoreAdminDetailPriorityColor,
  duviCoreAdminDetailStatusTone,
  duviCoreAdminCap,
  duviCoreAdminIsAdminRole,
} from "./use-duvi-core-admin-support-ticket-detail";

type Ticket = TicketDetail["ticket"];
type Message = TicketDetail["messages"][number];

function optionLabel(v: string): string {
  return v
    .split("-")
    .map((w) => duviCoreAdminCap(w))
    .join(" ");
}

const STATUS_OPTIONS = DUVI_CORE_ADMIN_TICKET_STATUS_SELECT.map((v) => ({
  value: v,
  label: optionLabel(v),
}));
const PRIORITY_OPTIONS = DUVI_CORE_ADMIN_TICKET_PRIORITY_SELECT.map((v) => ({
  value: v,
  label: optionLabel(v),
}));

export interface DuviCoreAdminSupportTicketDetailViewProps {
  ticket: Ticket | undefined;
  messages: Message[];
  isLoading: boolean;
  replyText: string;
  setReplyText: (v: string) => void;
  isInternalNote: boolean;
  setIsInternalNote: (v: boolean) => void;
  isSending: boolean;
  isResolving: boolean;
  isClosing: boolean;
  status: string;
  setStatus: (v: string) => void;
  priority: string;
  setPriority: (v: string) => void;
  handleSendReply: () => void;
  handleResolve: () => void;
  handleClose: () => void;
}

export function DuviCoreAdminSupportTicketDetailView(
  props: DuviCoreAdminSupportTicketDetailViewProps,
) {
  const {
    ticket,
    messages,
    isLoading,
    replyText,
    setReplyText,
    isInternalNote,
    setIsInternalNote,
    isSending,
    isResolving,
    isClosing,
    status,
    setStatus,
    priority,
    setPriority,
    handleSendReply,
    handleResolve,
    handleClose,
  } = props;

  if (isLoading || !ticket) {
    return (
      <div data-testid={DuviSharedAdminTestIds.ticketDetailPage}>
        <div className="py-16 text-center text-body-sm text-text-muted">
          Loading ticket…
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={DuviSharedAdminTestIds.ticketDetailPage}
      className="grid grid-cols-[70%_30%] gap-5 items-start max-[900px]:grid-cols-1"
    >
      <div className="flex flex-col gap-5">
        <div>
          <h1 className="text-h2 font-bold tracking-tight m-0 mb-3">
            {ticket.subject}
          </h1>
          <div className="flex gap-2 flex-wrap mb-3">
            <Badge variant="outline">
              <span
                className="w-2 h-2 rounded-full inline-block mr-1 align-middle"
                style={{
                  background: duviCoreAdminDetailPriorityColor(ticket.priority),
                }}
              />
              {duviCoreAdminCap(ticket.priority)}
            </Badge>
            <StatusPill status={duviCoreAdminDetailStatusTone(status)}>
              {optionLabel((status || ticket.status).replace(/_/g, "-"))}
            </StatusPill>
            <Badge variant="neutral">{ticket.category}</Badge>
          </div>
          <div className="flex items-center gap-2 text-text-secondary text-body-sm flex-wrap">
            <Avatar name={ticket.submitter_id} size="sm" />{" "}
            {ticket.submitter_id}
            <Badge variant="outline">{ticket.submitter_role}</Badge>
            <MonoChip>{ticket.submitter_id}</MonoChip>
          </div>
        </div>

        <div className="flex flex-col gap-3.5">
          {messages.map((m) => {
            const admin = duviCoreAdminIsAdminRole(m.author_role);
            return (
              <div
                key={m.msg_id}
                className={[
                  "flex gap-3 p-4 rounded-lg",
                  m.is_internal
                    ? "bg-warning-subtle border border-warning border-l-[3px]"
                    : admin
                      ? "bg-surface border border-border border-l-[3px] border-l-accent"
                      : "bg-surface border border-border",
                ].join(" ")}
              >
                <Avatar name={m.author_id} size="sm" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <b className="text-body-sm">{m.author_id}</b>
                    <Badge variant={admin ? "primary" : "outline"}>
                      {m.author_role}
                    </Badge>
                    {m.is_internal && (
                      <Badge variant="warning">Internal note</Badge>
                    )}
                    <span className="ml-auto text-text-muted text-caption">
                      {new Date(m.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="m-0 text-body-sm leading-relaxed text-text-primary">
                    {m.content}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <Card
          pad
          className={
            isInternalNote ? "ring-1 ring-inset ring-warning" : undefined
          }
        >
          <Textarea
            rows={4}
            data-testid={DuviSharedAdminTestIds.ticketReplyInput}
            placeholder={
              isInternalNote
                ? "Write an internal note (submitter won't see this)…"
                : "Write a reply to the submitter…"
            }
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
          />
          <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
            <Switch
              label="Internal note"
              data-testid={DuviSharedAdminTestIds.ticketInternalNoteToggle}
              checked={isInternalNote}
              onChange={(e) => setIsInternalNote(e.target.checked)}
            />
            <Button
              leftIcon={<Icon name="arrowUpRight" size={15} />}
              onClick={handleSendReply}
              disabled={isSending || !replyText.trim()}
              data-testid={DuviSharedAdminTestIds.ticketReplySubmit}
            >
              {isSending ? "Sending…" : "Send reply"}
            </Button>
          </div>
        </Card>
      </div>

      <div className="flex flex-col gap-5 sticky top-[88px]">
        <Card>
          <CardHeader
            title="Actions"
            icon={<Icon name="settings" size={18} />}
          />
          <CardBody>
            <div className="flex flex-col gap-3.5">
              <FormField label="Status">
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  options={STATUS_OPTIONS}
                />
              </FormField>
              <FormField label="Priority">
                <Select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  options={PRIORITY_OPTIONS}
                />
              </FormField>
              <div className="flex gap-2.5 mt-1">
                <Button
                  block
                  leftIcon={<Icon name="checkCircle" size={15} />}
                  onClick={handleResolve}
                  disabled={isResolving}
                  data-testid={DuviSharedAdminTestIds.ticketResolve}
                >
                  {isResolving ? "Resolving…" : "Resolve"}
                </Button>
                <Button
                  block
                  variant="destructive"
                  onClick={handleClose}
                  disabled={isClosing}
                  data-testid={DuviSharedAdminTestIds.ticketClose}
                >
                  {isClosing ? "Closing…" : "Close ticket"}
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Metadata" icon={<Icon name="info" size={18} />} />
          <CardBody>
            {[
              ["Created", new Date(ticket.created_at).toLocaleString()],
              ["Last updated", new Date(ticket.updated_at).toLocaleString()],
              ["Assigned level", ticket.assigned_level],
              ["Category", ticket.category],
            ].map(([k, v]) => (
              <div
                key={k}
                className="flex justify-between py-[7px] text-body-sm border-b border-border last:border-0"
              >
                <span className="text-text-muted">{k}</span>
                <span className="font-medium">{v}</span>
              </div>
            ))}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Related tickets"
            icon={<Icon name="message" size={18} />}
          />
          <CardBody>
            {/*
              Related tickets are not exposed by the API — there is no
              related-tickets endpoint in the core-platform contract. Render an
              honest empty state rather than fabricated sample rows.
            */}
            <p
              data-testid="ticket-related-empty"
              className="m-0 py-1 text-body-sm text-text-muted"
            >
              No related tickets.
            </p>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
